import type { AutomationTeamDto } from '@tms/shared';
import { useNavigate } from '@tanstack/react-router';
import { Save } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button, Dialog, Field, Input, Textarea } from '../../../components/ui';
import { useT } from '../../../i18n';
import { useDeleteTeam, useTeamDeletionImpact, useUpdateTeam } from '../hooks';
import { DangerZone, splitNames, useAutomationErrorText, useFailToast } from './shared';

/** What deleting the team takes along, counted by the API when it is asked for. */
const TeamImpact = ({ teamId }: { teamId: string }) => {
  const { t } = useT();
  const errorText = useAutomationErrorText();
  const impact = useTeamDeletionImpact(teamId, true);
  if (impact.error) return <p className="text-fail">{errorText(impact.error)}</p>;
  if (!impact.data) return <p>{t('automation.project.danger.counting')}</p>;
  return <p>{t('automation.project.teamSettings.impact', { ...impact.data })}</p>;
};

/** Name, description and members of a team; deleting it. Admins only. */
export const TeamSettingsDialog = ({
  team,
  onOpenChange,
}: {
  team: AutomationTeamDto;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useT();
  const navigate = useNavigate();
  const failToast = useFailToast();
  const update = useUpdateTeam(team.id);
  const remove = useDeleteTeam(team.id);
  // mounted when opened, so the form starts from the team as it is now
  const [name, setName] = useState(team.name);
  const [description, setDescription] = useState(team.description);
  const [members, setMembers] = useState(team.members.map((m) => m.name).join(', '));

  const save = () =>
    update.mutate(
      { name: name.trim(), description, memberNames: splitNames(members) },
      {
        onSuccess: () => {
          toast.success(t('automation.project.teamSettings.saved'));
          onOpenChange(false);
        },
        onError: failToast,
      },
    );

  const doDelete = () =>
    remove.mutate(undefined, {
      onSuccess: () => {
        toast.success(t('automation.project.teamSettings.deleted', { name: team.name }));
        onOpenChange(false);
        void navigate({ to: '/automation' });
      },
      onError: failToast,
    });

  return (
    <Dialog open onOpenChange={onOpenChange} title={t('automation.project.teamSettings.title')}>
      <div className="flex flex-col gap-4">
        <Field label={t('automation.project.field.name')} required>
          {({ id }) => (
            <Input id={id} value={name} onChange={(event) => setName(event.target.value)} />
          )}
        </Field>
        <Field label={t('common.description')}>
          {({ id }) => (
            <Textarea
              id={id}
              rows={2}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          )}
        </Field>
        <Field
          label={t('automation.project.createTeam.members')}
          hint={t('automation.project.teamSettings.membersHint')}
        >
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={members}
              placeholder={t('automation.project.createTeam.membersPlaceholder')}
              onChange={(event) => setMembers(event.target.value)}
            />
          )}
        </Field>
        <div className="flex justify-end gap-2">
          <Button onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            disabled={!name.trim()}
            loading={update.isPending}
            onClick={save}
          >
            <Save className="size-3.5" />
            {t('common.save')}
          </Button>
        </div>

        <DangerZone
          title={t('automation.project.teamSettings.dangerTitle')}
          text={t('automation.project.teamSettings.dangerText')}
          deleteLabel={t('automation.project.teamSettings.delete')}
          name={team.name}
          impact={<TeamImpact teamId={team.id} />}
          loading={remove.isPending}
          onDelete={doDelete}
        />
      </div>
    </Dialog>
  );
};
